import {
  type ReadonlyURLSearchParams,
  usePathname,
  useRouter,
} from "next/navigation";
import { LuSearch } from "react-icons/lu";

import { StickyStack } from "@/components/shared/sticky-stack";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useDebounceInputLogic } from "@/hooks/use-debounce-input-logic";

import { FEED_SEARCH_TAB_ITEMS } from "./feed-search-wrapper";

export const FeedSearchHeader = ({
  activeTab,
  searchParams,
  searchTerm,
}: {
  activeTab: string;
  searchParams: ReadonlyURLSearchParams;
  searchTerm: string;
}) => {
  const pathname = usePathname();
  const { replace } = useRouter();
  const { inputValue, handleInputChange } = useDebounceInputLogic({
    value: searchTerm,
    setValue: (term: string) => {
      const params = new URLSearchParams(searchParams);
      if (term) {
        params.set("search", term);
      } else {
        params.delete("search");
      }
      replace(`${pathname}?${params.toString()}`);
    },
  });

  return (
    <StickyStack
      className="
      z-10 flex flex-col gap-0 bg-background/80 backdrop-blur-lg
    "
    >
      <div
        className="
          flex w-full flex-col gap-2 p-4
          sm:flex-row sm:items-end sm:justify-between
        "
      >
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold text-muted-foreground">
            Showing Results for {searchTerm ? `'${searchTerm}'` : ""}{" "}
            {!searchTerm &&
              (activeTab === FEED_SEARCH_TAB_ITEMS.POSTS
                ? "All Posts"
                : "All Users")}
          </h2>
        </div>
      </div>
      <div className="relative m-4 mt-0 pb-0">
        <div
          className="
            absolute top-[10px] right-[14px] flex items-center gap-2 text-muted
          "
        >
          <LuSearch />
        </div>
        <Input
          value={inputValue}
          onChange={handleInputChange}
          placeholder="Search"
          className="w-full"
        />
      </div>
    </StickyStack>
  );
};

export const SearchResultsSkeleton = () => {
  return (
    <div
      className="
        sticky top-0 z-10 flex flex-col gap-0 border-b bg-background/0
        backdrop-blur-lg
      "
    >
      <div
        className="
          flex w-full flex-col gap-2 p-4
          sm:flex-row sm:items-end sm:justify-between
        "
      >
        <div className="flex flex-col gap-1">
          <Skeleton className="h-6 w-64" />
        </div>
      </div>
      <div className="relative m-4 mt-0 pb-0">
        <div
          className="
            absolute top-[10px] right-[14px] flex items-center gap-2 text-muted
          "
        >
          <LuSearch />
        </div>
        <Input disabled placeholder="Search" className="w-full" />
      </div>
    </div>
  );
};
